import { useCallback, useEffect, useReducer, useRef } from 'react';

import { cfetch } from '@confluence/network';

export type SpaceOrganizerRunStatus = 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'COMPLETED';

export type SpaceOrganizerRunResult = {
	runId: string;
	status: SpaceOrganizerRunStatus;
	createdSuggestionCount: number;
};

type SpaceOrganizerRunStartResult = {
	runId: string;
	status?: SpaceOrganizerRunStatus;
	createdSuggestionCount?: number;
};

export type LongTaskResult = {
	id: unknown;
	name: unknown;
	elapsedTime: number;
	percentageComplete: number;
	successful: boolean;
	finished: boolean;
	messages: unknown[];
	status: string;
	errors: unknown[] | null;
	additionalDetails: Record<string, unknown> | null;
};

export type UseRunSpaceOrganizerResult = {
	run: () => Promise<SpaceOrganizerRunResult>;
	isRunning: boolean;
	error: Error | undefined;
};

type RunState = {
	isRunning: boolean;
	error: Error | undefined;
};

type RunAction = { type: 'started' } | { type: 'succeeded' } | { type: 'failed'; error: Error };

type RunSpaceOrganizerOptions = {
	signal?: AbortSignal;
	pollIntervalMs?: number;
	backoffAfterMs?: number;
	backoffIntervalMs?: number;
	timeoutMs?: number;
};

const initialRunState: RunState = {
	isRunning: false,
	error: undefined,
};

const POLL_INTERVAL_MS = 2000;
const POLL_BACKOFF_AFTER_MS = 30000;
const POLL_BACKOFF_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const isActiveRunStatus = (status: SpaceOrganizerRunStatus): boolean =>
	status === 'QUEUED' || status === 'RUNNING';

const isSuccessfulRunStatus = (status: SpaceOrganizerRunStatus): boolean =>
	status === 'SUCCEEDED' || status === 'COMPLETED';

const runReducer = (state: RunState, action: RunAction): RunState => {
	switch (action.type) {
		case 'started':
			return {
				isRunning: true,
				error: undefined,
			};
		case 'succeeded':
			return { isRunning: false, error: undefined };
		case 'failed':
			return { ...state, isRunning: false, error: action.error };
		default:
			return state;
	}
};

const buildSpaceOrganizerRunUrl = (spaceKey: string): string =>
	`/wiki/rest/internal/1.0/spaces/${spaceKey}/organization/runs`;

const buildLongTaskUrl = (runId: string): string => `/wiki/rest/api/longtask/${runId}`;

const toError = (err: unknown): Error => (err instanceof Error ? err : new Error(String(err)));

const throwIfAborted = (signal?: AbortSignal) => {
	if (signal?.aborted) {
		throw new Error('Space Organizer run was cancelled');
	}
};

const getPollIntervalMs = (
	elapsedMs: number,
	{ pollIntervalMs, backoffAfterMs, backoffIntervalMs }: RunSpaceOrganizerOptions,
): number => {
	if (elapsedMs >= (backoffAfterMs ?? POLL_BACKOFF_AFTER_MS)) {
		return backoffIntervalMs ?? POLL_BACKOFF_INTERVAL_MS;
	}

	return pollIntervalMs ?? POLL_INTERVAL_MS;
};

const waitForPollInterval = (timeoutMs: number, signal?: AbortSignal): Promise<void> =>
	new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(new Error('Space Organizer run was cancelled'));
			return;
		}

		let timeoutId: ReturnType<typeof setTimeout>;

		const onAbort = () => {
			clearTimeout(timeoutId);
			reject(new Error('Space Organizer run was cancelled'));
		};

		timeoutId = setTimeout(() => {
			signal?.removeEventListener('abort', onAbort);
			resolve();
		}, timeoutMs);

		signal?.addEventListener('abort', onAbort, { once: true });
	});

const parseJsonResponse = async <T>(response: Response, errorPrefix: string): Promise<T> => {
	if (!response.ok) {
		throw new Error(`${errorPrefix}: ${response.status}`);
	}

	return (await response.json()) as T;
};

const postSpaceOrganizerRun = async (spaceKey: string): Promise<SpaceOrganizerRunStartResult> => {
	const response = await cfetch(buildSpaceOrganizerRunUrl(spaceKey), {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
		},
	});

	return parseJsonResponse<SpaceOrganizerRunStartResult>(response, 'Failed to run Space Organizer');
};

const getLongTask = async (runId: string): Promise<LongTaskResult> => {
	const response = await cfetch(buildLongTaskUrl(runId), {
		method: 'GET',
		credentials: 'same-origin',
		headers: {
			Accept: 'application/json',
		},
	});

	return parseJsonResponse<LongTaskResult>(response, 'Failed to poll Space Organizer run');
};

export const runSpaceOrganizer = async (
	spaceKey: string,
	options: RunSpaceOrganizerOptions = {},
): Promise<SpaceOrganizerRunResult> => {
	const startedAtMs = Date.now();
	const timeoutMs = options.timeoutMs ?? POLL_TIMEOUT_MS;
	const result = await postSpaceOrganizerRun(spaceKey);

	throwIfAborted(options.signal);

	if (!result.runId) {
		throw new Error('Failed to run Space Organizer: missing runId');
	}

	const runStatus = result.status ?? 'QUEUED';
	const createdSuggestionCount = result.createdSuggestionCount ?? 0;

	if (runStatus === 'FAILED') {
		throw new Error(`Failed to run Space Organizer: ${runStatus}`);
	}

	if (isSuccessfulRunStatus(runStatus)) {
		return { ...result, status: runStatus, createdSuggestionCount };
	}

	if (!isActiveRunStatus(runStatus)) {
		throw new Error(`Failed to run Space Organizer: ${runStatus}`);
	}

	while (true) {
		const elapsedMs = Date.now() - startedAtMs;

		if (elapsedMs >= timeoutMs) {
			throw new Error('Space Organizer run is still taking longer than expected');
		}

		await waitForPollInterval(
			Math.min(getPollIntervalMs(elapsedMs, options), timeoutMs - elapsedMs),
			options.signal,
		);

		const task = await getLongTask(result.runId);

		throwIfAborted(options.signal);

		if (!task.finished) {
			continue;
		}

		if (task.successful) {
			return { ...result, status: 'SUCCEEDED', createdSuggestionCount };
		}

		throw new Error('Failed to run Space Organizer: long task failed');
	}
};

export const useRunSpaceOrganizer = (spaceKey: string): UseRunSpaceOrganizerResult => {
	const [state, dispatch] = useReducer(runReducer, initialRunState);
	const activeRunPromiseRef = useRef<Promise<SpaceOrganizerRunResult> | undefined>(undefined);
	const abortControllerRef = useRef<AbortController | undefined>(undefined);

	useEffect(
		() => () => {
			abortControllerRef.current?.abort();
		},
		[],
	);

	const run = useCallback(async () => {
		if (activeRunPromiseRef.current) {
			return activeRunPromiseRef.current;
		}

		const abortController = new AbortController();

		abortControllerRef.current = abortController;
		dispatch({ type: 'started' });

		const activeRunPromise = runSpaceOrganizer(spaceKey, {
			signal: abortController.signal,
		})
			.then((result) => {
				if (!abortController.signal.aborted) {
					dispatch({ type: 'succeeded' });
				}

				return result;
			})
			.catch((err) => {
				const nextError = toError(err);

				if (!abortController.signal.aborted) {
					dispatch({ type: 'failed', error: nextError });
				}

				throw nextError;
			})
			.finally(() => {
				if (activeRunPromiseRef.current === activeRunPromise) {
					activeRunPromiseRef.current = undefined;
				}

				if (abortControllerRef.current === abortController) {
					abortControllerRef.current = undefined;
				}
			});

		activeRunPromiseRef.current = activeRunPromise;

		return activeRunPromise;
	}, [spaceKey]);

	return {
		run,
		isRunning: state.isRunning,
		error: state.error,
	};
};
